/**
 * OpenAI API wrapper that calls the deployed Supabase Edge Function
 */

import { buildSystemPrompt, getLevelDescriptor } from "../utils/systemPrompts";
import { invokeOpenAIFunction } from "./supabaseData";

export const OPENAI_MODELS = {
  DEFAULT: "gpt-5.4",
  QUEST: "gpt-5.4",
  ANALYSIS: "gpt-5.4",
  HELPER: "gpt-5-mini",
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const sanitizeText = (value, maxLength) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);

const parseJsonResult = (result) => {
  let text = (result?.result || "").trim();
  if (text.startsWith("```json")) text = text.replace(/^```json\s*/, "");
  if (text.startsWith("```")) text = text.replace(/^```\s*/, "");
  if (text.endsWith("```")) text = text.replace(/\s*```$/, "");
  return JSON.parse(text);
};
/**
 * Call the OpenAI API through the Supabase Edge Function proxy
 * @param {string} prompt - The prompt to send to OpenAI
 * @param {number} maxTokens - Maximum tokens for the response (default: 128)
 * @param {number} temperature - Temperature for response randomness (default: 0.7)
 * @param {Object} options - Optional request overrides such as model
 * @returns {Promise<Object>} - The response from OpenAI
 */
export const callOpenAI = async (prompt, maxTokens = 128, temperature = 0.7, options = {}) => {
  try {
    return await invokeOpenAIFunction({
      prompt,
      max_tokens: maxTokens,
      temperature,
      ...(options.model ? { model: options.model } : {}),
    });
  } catch (error) {
    console.error('Error calling OpenAI API:', error);
    throw error;
  }
};

export const validateManualQuestWithAI = async ({
  title = "",
  desc = "",
  xpHint = 30,
  questType = "standard",
  deadlineTime = null,
  durationMinutes = null,
  fixed = false,
  userLevel = 1,
} = {}) => {
  const prompt = buildSystemPrompt({
    contextLines: [
      `Review one manually drafted daily quest for a hunter at ${getLevelDescriptor(userLevel)}.`,
      `Draft title: ${title || "(empty)"}`,
      `Draft description: ${desc || "(empty)"}`,
      `XP hint from user: ${Number(xpHint) || 0}`,
      `Quest type chosen by user: ${questType || "standard"}`,
      `Deadline chosen by user: ${deadlineTime || "none"}`,
      `Duration chosen by user: ${durationMinutes || "none"}`,
      `Fixed quest: ${fixed ? "yes" : "no"}`,
    ],
    instructionLines: [
      "Validate whether this quest is specific, realistic, and verifiable within one day.",
      "Rewrite the title and description into plain, direct language.",
      "Set fair XP for the task and user level. Treat the XP hint as optional guidance only.",
      "Always generate one penalty quest tied to the same objective.",
      "Always generate one acceptance criteria line that makes pass or fail obvious.",
      "Do not change the user's quest type, deadline, or duration. Evaluate them as fixed constraints.",
      "If the written quest conflicts with those structured rules or is too vague to verify today, return valid false.",
      "Use normal daily-life wording. Avoid jargon, fluff, or dramatic phrasing.",
    ],
    outputLines: [
      'Return JSON only. Use exactly one of these shapes: {"valid":true,"title":"...","desc":"...","xp":number,"penaltyQuest":"...","acceptanceCriteria":"...","reviewNote":"..."} or {"valid":false,"error":"...","suggestion":"..."}.',
      "Title maximum 60 characters.",
      "Description maximum 120 characters.",
      "Penalty quest maximum 110 characters.",
      "Acceptance criteria maximum 110 characters.",
      "Review note maximum 160 characters.",
      "XP range 15 to 90.",
      "No markdown, no code fences, no extra commentary.",
    ],
  });

  const result = await callOpenAI(prompt, 700, 0.35, { model: OPENAI_MODELS.QUEST });
  const parsed = parseJsonResult(result);

  if (!parsed || typeof parsed !== "object") {
    throw new Error("The System returned invalid quest review data.");
  }

  if (parsed.valid === false) {
    return {
      valid: false,
      error: sanitizeText(parsed.error, 180) || "This quest is too vague to verify today.",
      suggestion: sanitizeText(parsed.suggestion, 180),
    };
  }

  if (parsed.valid !== true) {
    throw new Error("The System returned invalid quest review data.");
  }

  const reviewedQuest = {
    valid: true,
    title: sanitizeText(parsed.title, 60) || sanitizeText(title, 60),
    desc: sanitizeText(parsed.desc, 120),
    xp: clamp(Math.round(Number(parsed.xp) || Number(xpHint) || 20), 15, 90),
    penaltyQuest: sanitizeText(parsed.penaltyQuest, 110),
    acceptanceCriteria: sanitizeText(parsed.acceptanceCriteria, 110),
    reviewNote: sanitizeText(parsed.reviewNote, 160),
  };

  if (!reviewedQuest.title || !reviewedQuest.penaltyQuest || !reviewedQuest.acceptanceCriteria) {
    throw new Error("The System returned incomplete quest review data.");
  }

  return reviewedQuest;
};

/**
 * Generate motivational text for completing quests
 * @param {number} xpGained - The XP gained from completing quests
 * @returns {Promise<string>} - Motivational text
 */
export const generateMotivationalText = async (xpGained) => {
  const prompt = buildSystemPrompt({
    contextLines: [`A hunter just earned ${xpGained} XP from completed daily quests.`],
    instructionLines: [
      "Generate one brief motivational message.",
      "Acknowledge the achievement and encourage continued progress.",
    ],
    outputLines: [
      "Return plain text only.",
      "Maximum 120 characters.",
      "No labels or explanations.",
    ],
  });
  
  try {
    const result = await callOpenAI(prompt, 96, 0.9, { model: OPENAI_MODELS.HELPER });
    return result.result.trim();
  } catch (error) {
    console.error('Error generating motivational text:', error);
    return `Great job! You earned ${xpGained} XP!`;
  }
}; 

    /**
     * Generate a concise, clear quest description with Solo Leveling "system" tone.
     * Returns plain text (no labels), <= 140 chars. Grounded & realistic.
     * Accepts either a title+desc or a free-form brief.
     */
    export const generateQuestDescription = async ({
      title = "",
      description = "",
      userLevel = 1
    } = {}) => {
      const base = [title, description].filter(Boolean).join(" — ").trim() || "General productivity quest";
      const prompt = buildSystemPrompt({
        contextLines: [
          `Create one concise daily quest for a hunter at ${getLevelDescriptor(userLevel)}.`,
          `Task brief: "${base}"`,
        ],
        instructionLines: [
          "Use a realistic, actionable task based on the brief.",
        ],
        outputLines: [
          "Return plain text only.",
          "Maximum 140 characters.",
          "No quotes, no labels, no explanations.",
        ],
      });
      try {
        const result = await callOpenAI(prompt, 96, 0.7, { model: OPENAI_MODELS.HELPER });
        // expect { result: string }
        return (result?.result || "").trim();
      } catch (err) {
        console.error("Error generating quest description:", err);
        // safe fallback
        return (title || description || "Complete one meaningful task today").slice(0, 140);
      }
    };
